import { useEffect, useState } from 'react'
import { Scissors } from 'lucide-react'
import { Link, Outlet, useLocation } from 'react-router-dom'

import { OnlineBookingShareDialog } from '@/components/booking/OnlineBookingShareDialog'
import { OnboardingGate } from '@/components/onboarding/OnboardingWizard'
import { MobileMenuButton, MobileNavSheet, TopMenuBar } from '@/components/layout/TopMenuBar'
import { OnlineBookingToastHost } from '@/components/layout/OnlineBookingToastHost'
import { HeaderNotificationsPanel } from '@/components/layout/HeaderNotificationsPanel'
import { HeaderSearchDialog } from '@/components/layout/HeaderSearchDialog'
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher'
import { UserMenu } from '@/components/layout/UserMenu'
import { cn } from '@/lib/utils'

export function AppShell() {
  const location = useLocation()
  const isPosRoute = location.pathname === '/pos'
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  useEffect(() => {
    setMobileNavOpen(false)
  }, [location.pathname])

  return (
    <div
      className={cn(
        'salon-desk-mesh flex flex-col',
        isPosRoute ? 'pos-route h-dvh max-h-dvh overflow-hidden' : 'min-h-dvh',
      )}
    >
      <header
        className={cn(
          'app-topbar sticky top-0 z-50 shrink-0 overflow-visible border-b border-black/10 bg-[#8A6D42] text-white',
          isPosRoute ? 'px-2 lg:px-3' : 'px-3 lg:px-5',
        )}
      >
        <div
          className={cn(
            'flex h-[var(--app-topbar-height)] items-center gap-2 sm:gap-3',
            isPosRoute ? 'w-full max-w-none' : 'w-full',
          )}
        >
          {!isPosRoute ? <MobileMenuButton onOpen={() => setMobileNavOpen(true)} /> : null}

          <Link
            to="/"
            className="flex shrink-0 items-center gap-2 rounded-2xl transition-opacity hover:opacity-90"
            title="Today"
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-white/20 text-white shadow-md shadow-black/10 ring-1 ring-white/30">
              <Scissors className="h-4 w-4" strokeWidth={2.25} aria-hidden />
            </div>
            {!isPosRoute ? (
              <div className="hidden min-w-0 sm:block">
                <p className="text-sm font-semibold leading-none tracking-tight text-white">Salon Desk</p>
              </div>
            ) : (
              <p className="hidden text-sm font-semibold leading-none tracking-tight text-white sm:block">
                POS
              </p>
            )}
          </Link>

          {!isPosRoute ? (
            <>
              <TopMenuBar />
              <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-1.5">
                <LanguageSwitcher />
                <OnlineBookingShareDialog />
                <HeaderSearchDialog />
                <HeaderNotificationsPanel />
                <UserMenu />
              </div>
              <MobileNavSheet open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
            </>
          ) : (
            <div className="ml-auto flex shrink-0 items-center gap-1">
              <LanguageSwitcher />
              <HeaderNotificationsPanel />
              <UserMenu />
            </div>
          )}
        </div>
      </header>

      {!isPosRoute ? (
        <main className="min-h-0 flex-1 overflow-auto">
          <OnboardingGate>
            <Outlet />
          </OnboardingGate>
        </main>
      ) : (
        <main className="min-h-0 flex-1 overflow-hidden">
          <Outlet />
        </main>
      )}

      <OnlineBookingToastHost />
    </div>
  )
}
